<template>
    <div :class="['login', { 'has-error': isError }]">
        <LoginErrors v-if="isError"
                     :title="errorTitle"
                     :text="errorText"
                     :minutes="errorTime"
                     :is-timer="errorIsTimer"
                     :is-count="errorIsCounter"
                     @clearError="clearError" />

        <div v-else
             class="login__forms">
            <div class="login__form">
                <div class="login__form-logo">
                    <IconLogo class-name="icon-fill icon_color_blue" />
                </div>

                <div class="login__form-title">
                    Заполните данные
                </div>

                <div class="login__form-input indent_top_yellow">
                    <div class="input__wrapper">
                        <div class="input__container">
                            <input
                                id="name"
                                v-model.trim="name"
                                name="name"
                                placeholder=" "
                                :class="['input', {'is-error': $v.name.$dirty && (!$v.name.validFormat || !$v.name.minLength)}]"
                                @input="$v.name.$touch()"
                                @blur="$v.name.$touch()"
                                @focus="$v.name.$touch()">
                            <label for="name"
                                   class="input__label">
                                Ваше имя<sup>*</sup>
                            </label>
                            <span class="input__error">{{ getTextError('name') }}</span>
                        </div>
                    </div>
                </div>

                <div class="login__form-input">
                    <div class="input__wrapper">
                        <div class="input__container">
                            <input
                                id="surname"
                                v-model.trim="surname"
                                name="surname"
                                placeholder=" "
                                :class="['input', {'is-error': $v.surname.$dirty && (!$v.surname.validFormat || !$v.surname.minLength)}]"
                                @input="$v.surname.$touch()"
                                @blur="$v.surname.$touch()"
                                @focus="$v.surname.$touch()">
                            <label for="surname"
                                   class="input__label">
                                Ваша фамилия<sup>*</sup>
                            </label>
                            <span class="input__error">{{ getTextError('surname') }}</span>
                        </div>
                    </div>
                </div>

                <div class="login__form-input">
                    <div class="input__wrapper">
                        <div class="input__container">
                            <input
                                id="patronymic"
                                v-model.trim="patronymic"
                                name="patronymic"
                                placeholder=" "
                                :class="['input', {'is-error': $v.patronymic.$dirty && !$v.patronymic.validFormat}]"
                                @input="$v.patronymic.$touch()"
                                @blur="$v.patronymic.$touch()"
                                @focus="$v.patronymic.$touch()">
                            <label for="patronymic"
                                   class="input__label">
                                Ваше отчество
                            </label>
                            <span class="input__error">{{ getTextError('patronymic') }}</span>
                        </div>
                    </div>
                </div>

                <div class="login__form-input">
                    <div class="input__wrapper">
                        <div class="input__container">
                            <input
                                id="email"
                                v-model.trim="email"
                                name="email"
                                placeholder=" "
                                :class="['input', {'is-error': $v.email.$dirty && !$v.email.validFormat}]"
                                @input="$v.email.$touch()"
                                @blur="$v.email.$touch()"
                                @focus="$v.email.$touch()">
                            <label for="email"
                                   class="input__label">
                                Ваш E-mail<sup>*</sup>
                            </label>
                            <span class="input__error">Адрес электронной почты введён неправильно</span>
                        </div>
                    </div>
                </div>

                <div class="login__form-input">
                    <div class="input__wrapper">
                        <div class="input__container">
                            <input
                                id="agency"
                                v-model.trim="agency"
                                name="agency"
                                placeholder=" "
                                :class="['input', {'is-error': $v.agency.$dirty && !$v.agency.minLength}]"
                                @input="$v.agency.$touch()"
                                @blur="$v.agency.$touch()"
                                @focus="$v.agency.$touch()">
                            <label for="agency"
                                   class="input__label">
                                Название агентства<sup>*</sup>
                            </label>
                            <span class="input__error">Название агентства введёно неправильно</span>
                        </div>
                    </div>
                </div>

                <div class="login__form-submit">
                    <STBaseButton text="Сохранить"
                                  :disabled="isInputsFill || loading"
                                  modify="button_full_width"
                                  @click="save" />
                </div>

                <STLoader :loading="loading"
                          :has-loaded="hasLoaded"
                          :message="loaderMessage"
                          :title="loaderTitle"
                          modify="loader_full_size" />
            </div>
        </div>

        <div class="login__footer">
            <a
                class="login__footer-phone"
                href="tel:74957974949">+7 (495) 797-49–49</a>
            <STBaseButton
                text="Сообщить о проблеме"
                icon-before="IconAlertOutlineWhite"
                icon-before-modify="icon-fill"
                modify="login__footer-action login__footer-report button_theme_reset"
                href="/report-problems" />
        </div>
    </div>
</template>

<script>
import IconLogo from '@/components/Icons/Logo';
import LoginErrors from '../Errors/Error';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';
import {mapActions, mapGetters} from 'vuex';
import {isProfileComplete, PROFILE_FILLING_PATH} from '@/helpers/authRedirect';
import { errorTimeMessage } from '@/helpers/errorHelper';
import Utils from '@/helpers/Utils';

export default {
    name: 'ProfileFillingForm',

    components: {
        IconLogo,
        LoginErrors
    },

    validations: {
        name: {
            required,
            minLength  : minLength(1),
            maxLength  : maxLength(240),
            validFormat: value => /^[A-Za-zА-Яа-яЁё]+$/.test(value)
        },
        surname: {
            required,
            minLength  : minLength(1),
            maxLength  : maxLength(240),
            validFormat: value => /^[A-Za-zА-Яа-яЁё]+$/.test(value)
        },
        patronymic: {
            maxLength  : maxLength(240),
            validFormat: value => !value || /^[A-Za-zА-Яа-яЁё]+$/.test(value)
        },
        email: {
            required,
            minLength  : minLength(3),
            maxLength  : maxLength(300),
            validFormat: value => Utils.validMail(value)
        },
        agency: {
            required,
            minLength  : minLength(1),
            maxLength  : maxLength(240),
            validFormat: value => /^[A-Za-zА-Яа-яЁё]+$/.test(value)
        }
    },

    data() {
        return {
            name            : '',
            surname         : '',
            patronymic      : '',
            email           : '',
            agency          : '',
            loaderTitle     : null,
            loaderMessage   : 'Сохранение данных',
            loading         : false,
            hasLoaded       : false,
            timeShowResult  : 1500,
            redirectTimer   : null,
            isError         : false,
            errorTitle      : '',
            errorText       : '',
            errorTime       : 0,
            errorIsTimer    : false,
            errorIsCounter  : false,
            isPrefillApplied: false
        };
    },

    computed: {
        ...mapGetters({
            userData      : 'GET_USER_DATA',
            userDataLoaded: 'GET_IS_USER_DATA_LOADED'
        }),

        isInputsFill() {
            return this.$v.name.$invalid || this.$v.surname.$invalid || this.$v.email.$invalid || !this.agency;
        }
    },

    watch: {
        userData: {
            immediate: true,
            handler(data) {
                this.applyUserData(data);
            }
        }
    },

    beforeDestroy() {
        clearTimeout(this.redirectTimer);
    },

    methods: {
        ...mapActions(['fillProfile']),

        applyUserData(data) {
            if (this.isPrefillApplied || !this.userDataLoaded) {
                return;
            }

            this.name = (data && data.firstName) || '';
            this.surname = (data && (data.lastName || data.surname)) || '';
            this.patronymic = (data && data.middleName) || '';
            this.email = (data && data.email) || '';
            this.agency = (data && data.agencies && data.agencies[0] && data.agencies[0].agencyName) || '';
            this.isPrefillApplied = true;
        },

        /**
         * Сохранение недостающих данных профиля
         */
        async save() {
            try {
                if (this.loading || this.hasLoaded || this.isInputsFill) {
                    return;
                }

                this.launchLoader(true, 'Сохранение данных');
    
                await this.fillProfile({
                    firstName : this.name,
                    lastName  : this.surname,
                    middleName: this.patronymic.length ? this.patronymic : null,
                    email     : this.email,
                    agency    : this.agency
                })
                    .then((response) => {
                        const {data} = response;
    
                        if (data.error) {
                            if (data.notCounter) {
                                this.showError({
                                    code: 1
                                });
                            } else {
                                this.showError({
                                    code: 3
                                });
                            }
                            this.launchLoader(false);
                            return;
                        }
    
                        // messageCode
                        // 0 - Ошибка интеграции
                        // 1 - Данные обработаны
                        // 2 - Истёк срок жизни AccessToken
                        // 3 - Некорректный AccessToken
                        // 4 - Пользователь не найден
                        const isShowError = !data.data || data.data.messageCode !== 1 || !isProfileComplete(data.data);
    
                        if (isShowError) {
                            this.showError({
                                code: 1
                            });
                            this.launchLoader(false);
                            return;
                        }
    
                        this.loaderTitle = 'Данные сохранены';
                        this.loaderMessage = 'Вы будете перенаправлены<br/>в личный кабинет';
                        this.completeLoader();
    
                        this.redirectTimer = setTimeout(() => {
                            this.redirectTimer = null;
                            this.goToApp();
                        }, this.timeShowResult);
                    })
                    .catch((error) => {
                        this.launchLoader(false);
    
                        if (!this.isError) {
                            this.showError({
                                code: 5,
                                data: (error && error.response) || {}
                            });
                        }
                        return;
                    });
            } catch (error) {
                this.launchLoader(false);

                if (!this.isError) {
                    this.showError({
                        code: 5,
                        data: (error && error.response) || {}
                    });
                }
            }
        },

        goToApp() {
            const {redirect: redirectPath, ...query} = this.$route.query;

            if (redirectPath && redirectPath !== PROFILE_FILLING_PATH) {
                this.$router.push({
                    path: `${redirectPath}`,
                    query
                }).catch(() => {});

                return;
            }

            this.$router.push('/appointments').catch(() => {});
        },

        /**
         * Показывает ошибку
         * @param code {Number} - код ошибки. 1 - ошибка обработки запроса в CRM. 2 - Блокировка на время. 3 - Ошибка со счётчиком попыток. 4 - Блокировка при привышении кол-ва попыток. 5 - Ошибка на сервере
         * @param data {Object} - данные для показа ошибки
         */
        showError({code, data = {}}) {
            switch (code) {
                case 1:
                    this.errorTitle = 'Ошибка';
                    this.errorText = 'К&nbsp;сожалению, произошла техническая ошибка. Попробуйте еще раз позже или перезагрузите страницу.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = false;
                    break;
                case 2:
                    this.errorTitle = 'Доступ временно заблокирован';
                    this.errorText = data.message || 'Вы превысили лимит на количество запросов. Ваш аккаунт временно заблокирован.';
                    this.errorTime = +data.time || 20;
                    this.errorIsTimer = true;
                    this.errorIsCounter = false;
                    break;
                case 3:
                    this.errorTitle = 'Ошибка';
                    this.errorText = 'К&nbsp;сожалению, произошла техническая ошибка. Попробуйте еще раз или повторите попытку позже.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = true;
                    break;
                case 4:
                    this.errorTitle = 'Авторизация недоступна';
                    this.errorText = 'К&nbsp;сожалению, по&nbsp;техническим причинам в&nbsp;данный момент авторизация недоступна и&nbsp;мы&nbsp;уже работаем над решением проблемы. Попробуйте еще раз позже.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = true;
                    break;
                case 5:
                    const limitErrorCode = 429;

                    if (data && data?.status === limitErrorCode) {
                        const limitErrorTime = data?.data?.seconds || 20;
                        const minutesBlock = limitErrorTime / 60;
                        const limitErrorMessage = errorTimeMessage(limitErrorTime);

                        this.errorTitle = 'Доступ временно заблокирован';
                        this.errorText = limitErrorMessage;
                        this.errorTime = minutesBlock;
                        this.errorIsTimer = true;
                        this.errorIsCounter = false;

                        break;
                    }

                    this.errorTitle = 'Ошибка';
                    this.errorText = 'К сожалению, произошла техническая ошибка. Попробуйте еще раз позже или перезагрузите страницу.';
                    this.errorTime = 0;
                    this.errorIsTimer = false;
                    this.errorIsCounter = false;

                    break;
                default:
                    break;
            }

            this.isError = true;
        },

        clearError() {
            this.isError = false;
        },

        launchLoader(status, message = '') {
            this.loading = status;
            this.loaderMessage = message;
        },

        completeLoader() {
            this.hasLoaded = true;
            this.launchLoader(false, this.loaderMessage);
        },

        getTextError(type) {
            const fieldLength = this[type].length;

            if (fieldLength < 1) {
                return 'Это поле обязательно для заполнения';
            }

            return 'Без цифр и спец. символов';
        }
    }
};
</script>

<style lang="scss">
@import "../../Login";
</style>
